import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/arrow-down.svg';
import OutsideClickHandler from 'react-outside-click-handler';
import './Select.scss';
import { formatInTimeZone } from 'date-fns-tz';

export interface SelectOption {
  key: any;
  value: any;
}

interface SelectProps {
  title?: string;
  options: SelectOption[];
  selectedOption: any;
  onChange: (option: SelectOption, name: string) => void;
  placeholder?: string;
  name: string;
  isError: boolean;
  errorMessage?: string;
  openMenuToTop?: boolean;
  maxHeight?: string;
  isObjectValue?: boolean;
  onSearch?: (searchTerm: string) => void;
  enableServerSearch?: boolean;
  isLoading?: boolean;
  forceOpen?: boolean;
  isDisable?: boolean;
  preventAutoOpenOnLoad?: boolean;
  useMenuPortal?: boolean;
}

const Select: React.FC<SelectProps> = ({
  options,
  selectedOption,
  onChange,
  placeholder,
  name,
  errorMessage,
  isError,
  title,
  openMenuToTop,
  maxHeight,
  isObjectValue,
  onSearch,
  enableServerSearch = false,
  isLoading = false,
  forceOpen = false,
  isDisable = false,
  preventAutoOpenOnLoad = false,
  useMenuPortal = false,
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSearchingRef = useRef(false);
  const shouldKeepOpenRef = useRef(false);

  const isMenuOpen = isDropdownVisible || forceOpen;

  const updateMenuPosition = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const resolvedMaxHeight = maxHeight || '300px';

    if (openMenuToTop) {
      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        bottom: window.innerHeight - rect.top + 8,
        maxHeight: resolvedMaxHeight,
        zIndex: 2100,
      });
      return;
    }

    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      maxHeight: resolvedMaxHeight,
      zIndex: 2100,
    });
  }, [maxHeight, openMenuToTop]);

  // Use server-side search if enabled, otherwise use client-side filtering
  const filteredOptions = enableServerSearch 
    ? options 
    : options.filter((option) =>
        option.value.toLowerCase().includes(searchQuery.toLowerCase())
      );

  // Prevent dropdown from closing when options change during server search
  useEffect(() => {
    if (enableServerSearch && shouldKeepOpenRef.current) {
      // Force dropdown to stay open during server search
      setIsDropdownVisible(true);
    }
  }, [options, enableServerSearch]);

  const onOptionSelect = (option: SelectOption) => {
    onChange(option, name);
    setSearchQuery('');
    setIsDropdownVisible(false);
  };

  const handleOutsideClick = () => {
    // Don't close if we're currently searching or should keep open
    if (isSearchingRef.current || shouldKeepOpenRef.current || forceOpen) {
      return;
    }
    
    // Only close if not searching and not clicking inside the dropdown
    if (!isSearching && dropdownRef.current && !dropdownRef.current.contains(document.activeElement)) {
      setIsDropdownVisible(false);
      setSearchQuery('');
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle escape key to close dropdown
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDropdownVisible) {
        setIsDropdownVisible(false);
        setSearchQuery('');
        shouldKeepOpenRef.current = false;
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownVisible]);

  // Keep dropdown open when loading (unless preventAutoOpenOnLoad is true)
  useEffect(() => {
    if (isLoading && !preventAutoOpenOnLoad) {
      shouldKeepOpenRef.current = true;
      setIsDropdownVisible(true);
    } else if (isLoading && preventAutoOpenOnLoad) {
      // Ensure dropdown stays closed when loading if preventAutoOpenOnLoad is true
      setIsDropdownVisible(false);
    }
    // Don't auto-reset shouldKeepOpenRef - let parent control it
  }, [isLoading, preventAutoOpenOnLoad]);

  useEffect(() => {
    if (!useMenuPortal || !isMenuOpen) return;

    updateMenuPosition();

    const handleScrollOrResize = () => updateMenuPosition();
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [useMenuPortal, isMenuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!useMenuPortal || !isMenuOpen) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (isSearchingRef.current || shouldKeepOpenRef.current || forceOpen) {
        return;
      }

      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }

      setIsDropdownVisible(false);
      setSearchQuery('');
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [useMenuPortal, isMenuOpen, forceOpen]);

  const getSelectedValueString = () => {
    if (isDropdownVisible) return searchQuery;

    if (isObjectValue && selectedOption.value) {
      const obj = JSON.parse(selectedOption.value);
      return `${obj.title} - ${formatInTimeZone(
        new Date(obj.scheduled_time),
        'Europe/London',
        'dd/MM/yyyy'
      )} - ${formatInTimeZone(new Date(obj.scheduled_time), 'Europe/London', 'hh:mm a')}`;
    } else {
      return selectedOption?.value || '';
    }
  };

  const getValue = (value: string) => {
    const obj = JSON.parse(value);
    return `${obj.title} - ${formatInTimeZone(
      new Date(obj.scheduled_time),
      'Europe/London',
      'dd/MM/yyyy'
    )} - ${formatInTimeZone(new Date(obj.scheduled_time), 'Europe/London', 'hh:mm a')}`;
  };

  const dropdownMenu = isMenuOpen ? (
    <div
      id={listboxId}
      ref={dropdownRef}
      role='listbox'
      className={`select-dropdown${useMenuPortal ? ' select-dropdown--portal' : ''}`}
      style={
        useMenuPortal
          ? menuStyle
          : {
              top:
                openMenuToTop && !title
                  ? '-168px'
                  : openMenuToTop && title
                    ? '-132px'
                    : undefined,
              maxHeight: maxHeight,
            }
      }
    >
      {isLoading ? (
        <div className='select-loading'>Searching...</div>
      ) : filteredOptions.length > 0 ? (
        filteredOptions.map((option, i) => (
          <div
            key={`${option.key}-${i}`}
            className='select-option'
            onClick={() => onOptionSelect(option)}
          >
            {isObjectValue ? getValue(option.value) : option.value}
          </div>
        ))
      ) : (
        <div className='select-no-options'>No matches found</div>
      )}
    </div>
  ) : null;

  const handleToggleDropdown = () => {
    if (isDisable) return;
    setIsDropdownVisible((prev) => !prev);
  };

  const inputSection = (
    <div
      className={`select-input-container${isDisable ? ' select-input-container--disabled' : ''}`}
      onClick={handleToggleDropdown}
      onKeyDown={(e) => {
        if (isDisable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggleDropdown();
        }
      }}
      role='combobox'
      aria-controls={listboxId}
      aria-expanded={isMenuOpen}
      aria-haspopup='listbox'
      tabIndex={isDisable ? -1 : 0}
    >
      {title && <div className='title'>{title}</div>}

      <input
        type='text'
        value={getSelectedValueString()}
        disabled={isDisable}
        readOnly={!enableServerSearch}
        onChange={(e) => {
          const newSearchQuery = e.target.value;
          setSearchQuery(newSearchQuery);

          if (!isDropdownVisible) {
            setIsDropdownVisible(true);
          }

          if (enableServerSearch && onSearch) {
            setIsSearching(true);
            isSearchingRef.current = true;
            shouldKeepOpenRef.current = true;

            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
            }

            searchTimeoutRef.current = setTimeout(() => {
              setIsSearching(false);
              isSearchingRef.current = false;
            }, 1000);

            onSearch(newSearchQuery);
          }
        }}
        placeholder={!isDropdownVisible ? placeholder : undefined}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleDropdown();
        }}
        className={`select-input ${isError && 'error'} ${
          isDropdownVisible && 'outlined'
        }`}
      />
      <ArrowDownIcon
        className={`select-arrow ${isDropdownVisible && 'rotated'}`}
      />
      {errorMessage && <div className='error-message'>{errorMessage}</div>}
    </div>
  );

  return (
    <div className='select-container' ref={containerRef}>
      {useMenuPortal ? (
        inputSection
      ) : (
        <OutsideClickHandler onOutsideClick={handleOutsideClick}>
          {inputSection}
          {dropdownMenu}
        </OutsideClickHandler>
      )}
      {useMenuPortal &&
        dropdownMenu &&
        createPortal(dropdownMenu, document.body)}
    </div>
  );
};

export default Select;
